import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { listItems } from '../api';
import { useWishlist } from '../hooks/useWishlist';
import useBrowseFilters from '../hooks/useBrowseFilters';
import JewelleryCard from '../components/JewelleryCard';
import ListingImage from '../components/ListingImage';
import ContactOwnerSheet from '../components/ContactOwnerSheet';
import JewelleryMetalNotice from '../components/JewelleryMetalNotice';
import MobileFilterPanel from '../components/MobileFilterPanel';
import FilterFields from '../components/FilterFields';
import {
  JEWELLERY_CATEGORIES, PRIMARY_COLOURS, FINISHES, STONE_WORK_TYPES
} from '../components/JewellerySpecFields';

const SEARCH_DEBOUNCE_MS = 300;

// fix_issue3.md #2 - "Sort by High to Low Price" and "Best to Low ratings" are the two the
// requirement calls out; the recommended feed and low-to-high price round it out.
const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'rating', label: 'Rating: Best to Low' }
];

// Structured browse filters, matched 1:1 with the listing form's spec fields
// (JewellerySpecFields.js) and rentalapi's ?category/&primaryColour/&stoneWorkType/&finish query
// params.
const opts = (arr) => arr.filter((o) => o !== 'Other').map((o) => ({ value: o, label: o }));
const FILTERS = [
  { key: 'category', label: 'Category', options: opts(JEWELLERY_CATEGORIES) },
  { key: 'primaryColour', label: 'Colour', options: opts(PRIMARY_COLOURS) },
  { key: 'stoneWorkType', label: 'Stone / work', options: opts(STONE_WORK_TYPES) },
  { key: 'finish', label: 'Finish', options: opts(FINISHES) }
];
const FILTER_KEYS = FILTERS.map((f) => f.key);

// Jewellery rental - ₹15,000 covers even a high-end bridal set for a week.
const PRICE_PROPS = { ceiling: 15000 };

// homepage/user_experience.md #1 - a deep initial page + keep-loading-more.
const PAGE_SIZE = 50;
const SCROLL_KEY = 'rv:listings';

// #1 - a compact "Trending now" rail above the grid, shown only on the pristine browse view.
// Deliberately NOT .jewellery-card / .card markup so the results grid stays the first card on the page.
function TrendingStrip({ items, browsePath }) {
  if (!items.length) return null;
  return (
    <section aria-label="Trending now" className="mb-2">
      <h5 className="mb-2">Trending now</h5>
      <div className="rv-trending">
        {items.map((item) => (
          <Link
            key={item._id}
            to={`/items/${item._id}`}
            state={{ from: browsePath }}
            className="rv-trending-card"
          >
            {item.images?.[0] && (
              <ListingImage
                src={item.images[0]}
                alt={item.name}
                sizes="(max-width: 576px) 46vw, 200px"
                style={{ width: '100%', height: 130, objectFit: 'cover' }}
              />
            )}
            <div className="p-2">
              <div className="small fw-semibold text-truncate">{item.name}</div>
              <div className="small text-muted text-truncate">{item.location}</div>
              <div className="small">₹{item.price}/{item.priceUnit}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ItemList() {
  const browse = useBrowseFilters(FILTER_KEYS);
  useEffect(() => { setSearchInput(browse.q); }, [browse.q]);
  const location = useLocation();
  const [searchInput, setSearchInput] = useState(browse.q);
  const [localities, setLocalities] = useState([]);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [contactItem, setContactItem] = useState(null);

  const { has: isWishlisted, toggle: toggleWishlist } = useWishlist();

  const isPristine = !browse.q && browse.activeCount === 0 && browse.sort === 'recommended';
  // #1 - the "Trending now" rail is just the top of the (recommended-sorted) pristine feed - no
  // extra request, and it never appears once a search / filter / sort is active.
  const trending = isPristine && items.length >= 12 ? items.slice(0, 10) : [];
  const restoredRef = useRef(false);

  // Debounce the search box into ?q so results update live while staying shareable/bookmarkable.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput.trim() !== browse.q) browse.setParam('q', searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Reset to page 1 and refetch whenever the search term, sort, or any filter changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setPage(1);
    listItems({ ...browse.apiParams, limit: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data || []);
        setLocalities(res.localities || []);
        setPagination(res.pagination || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Listings load failed:', err.response?.status, err.response?.data || err.message);
        setError("We couldn't load listings right now. Please try again in a moment.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browse.sig]);

  // #3 - restore scroll when the customer returns from a listing; remember it when we leave.
  useEffect(() => {
    if (loading || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved && saved.url === `${location.pathname}${location.search}`) {
        window.scrollTo(0, saved.y || 0);
      }
    } catch { /* ignore */ }
  }, [loading, location.pathname, location.search]);

  const rememberScroll = useCallback(() => {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({
        url: `${location.pathname}${location.search}`,
        y: window.scrollY
      }));
    } catch { /* ignore */ }
  }, [location.pathname, location.search]);

  useEffect(() => rememberScroll, [rememberScroll]);

  const resultCount = pagination?.total ?? items.length;

  const loadMore = async () => {
    if (!pagination?.hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await listItems({ ...browse.apiParams, page: nextPage, limit: PAGE_SIZE });
      setItems((prev) => [...prev, ...(res.data || [])]);
      setPagination(res.pagination || null);
      setPage(nextPage);
    } catch {
      setError('Could not load more listings right now.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div>
      <JewelleryMetalNotice />

      <Form className="mb-3" onSubmit={(e) => e.preventDefault()}>
        <Form.Control
          type="search"
          aria-label="Search jewellery"
          placeholder="Search jewellery by name, material, or style..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </Form>

      {/* Wide screens: filters inline. */}
      <div className="d-none d-lg-flex flex-wrap gap-2 align-items-end mb-4">
        <FilterFields localities={localities} browse={browse} filters={FILTERS} sortOptions={SORT_OPTIONS} priceProps={PRICE_PROPS} />
        {browse.activeCount > 0 && (
          <Button variant="link" size="sm" className="px-0 text-decoration-none" onClick={browse.clearAll}>
            Clear filters ({browse.activeCount})
          </Button>
        )}
      </div>

      {/* Narrow screens: a floating button opens the right-hand filter panel. */}
      <MobileFilterPanel
        activeCount={browse.activeCount}
        resultCount={loading ? undefined : resultCount}
        resultNoun="piece"
        onClearAll={browse.clearAll}
      >
        <FilterFields localities={localities} browse={browse} filters={FILTERS} sortOptions={SORT_OPTIONS} priceProps={PRICE_PROPS} stacked />
      </MobileFilterPanel>

      {!loading && !error && isPristine && <TrendingStrip items={trending} browsePath={browse.browsePath} />}

      {loading && <Spinner animation="border" role="status" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && items.length === 0 && (
        <Alert variant="light">
          {browse.activeCount || browse.q
            ? 'No pieces match these filters. Try clearing a filter or widening your search.'
            : 'No pieces are listed yet — please check back soon.'}
        </Alert>
      )}

      {!loading && !error && items.length > 0 && (
        <h5 className="mb-3">{isPristine ? 'All listings' : `${resultCount} ${resultCount === 1 ? 'result' : 'results'}`}</h5>
      )}

      <Row xs={1} sm={2} lg={3} xl={4} className="g-3">
        {items.map((item, i) => (
          <Col key={item._id}>
            <JewelleryCard
              item={item}
              eager={i < 4}
              wishlisted={isWishlisted(item._id)}
              onToggleWishlist={toggleWishlist}
              onContact={setContactItem}
              browsePath={browse.browsePath}
              onNavigate={rememberScroll}
            />
          </Col>
        ))}
      </Row>

      {!loading && pagination?.hasMore && (
        <div className="text-center mt-4">
          <Button variant="outline-primary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}

      <ContactOwnerSheet item={contactItem} show={!!contactItem} onHide={() => setContactItem(null)} />
    </div>
  );
}
